import { AbilityBuilder, createMongoAbility, PureAbility } from '@casl/ability';
import { ApiAction, ApiSubject, ApiUserPermission } from './ApiSchemas/RecordTypes/ApiUserOrgSchema';

export type AppAbility = PureAbility<[ApiAction, ApiSubject]>;

/**
 * Creates a CASL ability instance from a permissions array (as returned by the API).
 * This allows client-side permission checking that mirrors the server-side implementation.
 *
 * Note: The `manage` action is excluded because it doesn't round-trip through serialization.
 * The server may grant `manage` broadly but deny specific actions via `cannot` rules.
 * Since the API already probes each individual action, the concrete actions in the
 * permissions array fully describe what the user can do — `manage` is redundant at best
 * and incorrect at worst.
 */
export const createAbilityFromPermissions = (permissions: ApiUserPermission[]): AppAbility => {
  const builder = new AbilityBuilder<AppAbility>(createMongoAbility);
  for (const { subject, actions } of permissions) {
    for (const action of actions) {
      if (action !== 'manage') {
        builder.can(action, subject);
      }
    }
  }
  return builder.build();
};

export const userCan = (ability: AppAbility | undefined, action: ApiAction, subject: ApiSubject): boolean => ability?.can(action, subject) ?? false;

export const validateUserCan = (ability: AppAbility | undefined, action: ApiAction, subject: ApiSubject): void => {
  if (!userCan(ability, action, subject)) {
    throw new Error(`Access denied: cannot ${action} ${subject}`);
  }
};
